import {
  blue,
  gold,
  gray,
  green,
  orange,
  presetDarkPalettes,
  red
} from '@ant-design/colors'
import { Badge, Tag, theme } from 'antd'
import type { TagProps } from 'antd'
import { createStyles } from 'antd-style'
import { forwardRef } from 'react'
import type { ReactNode } from 'react'

import { useThemeMode } from 'src/theme'
import type { ThemeMode } from 'src/theme'

export type StatusTone = 'gray' | 'green' | 'gold' | 'orange' | 'red' | 'blue'

export type ToneShades = {
  wash: string
  chip: string
  border: string
  solid: string
  text: string
}

const TONE_PALETTE: Record<StatusTone, string[]> = {
  gray,
  green,
  gold,
  orange,
  red,
  blue
}

// AntD's dark presets run from deep (0) to light (9), so the same indices give
// a dark wash/chip/border; text steps one lighter to stay readable on them.
const DARK_TONE_PALETTE: Record<StatusTone, string[]> = {
  gray: presetDarkPalettes.grey,
  green: presetDarkPalettes.green,
  gold: presetDarkPalettes.gold,
  orange: presetDarkPalettes.orange,
  red: presetDarkPalettes.red,
  blue: presetDarkPalettes.blue
}

const WASH = 0
const CHIP = 1
const BORDER = 2
const SOLID = 5
const TEXT = 7
const DARK_TEXT = 8

export const toneShades = (
  tone: StatusTone,
  mode: ThemeMode = 'light'
): ToneShades => {
  const palette = (mode === 'dark' ? DARK_TONE_PALETTE : TONE_PALETTE)[tone]

  return {
    wash: palette[WASH],
    chip: palette[CHIP],
    border: palette[BORDER],
    solid: palette[SOLID],
    text: palette[mode === 'dark' ? DARK_TEXT : TEXT]
  }
}

const useStyles = createStyles(() => ({
  pill: {
    marginInlineEnd: 0,

    '& .ant-badge-status-text': {
      marginInlineStart: 6
    }
  }
}))

export type StatusPillProps = {
  tone: StatusTone
  children: ReactNode
  bordered?: boolean
} & Omit<TagProps, 'color' | 'children' | 'bordered'>

export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ tone, children, className, style, bordered = true, ...rest }, ref) => {
    const { styles, cx } = useStyles()
    const { token } = theme.useToken()
    const { mode } = useThemeMode()
    const shades = toneShades(tone, mode)
    const { fontSize, ...tagStyle } = style ?? {}

    return (
      <Tag
        ref={ref}
        className={cx(styles.pill, className)}
        style={{
          background: shades.chip,
          border: bordered ? `1px solid ${shades.border}` : 'none',
          borderRadius: 999,
          color: shades.text,
          ...tagStyle
        }}
        {...rest}
      >
        <Badge
          color={shades.solid}
          text={
            <span
              style={{
                color: shades.text,
                fontSize: fontSize ?? token.fontSizeSM
              }}
            >
              {children}
            </span>
          }
        />
      </Tag>
    )
  }
)

StatusPill.displayName = 'StatusPill'
